<script>
import TrucksMixin from "@/views/fleets/mixins/TrucksMixin.vue";
import UsersMixin from "@/views/users/mixins/UsersMixin.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import TruckChangeStatus from "@/components/truck/TruckChangeStatus.vue";
import BaseView from "@/mixins/BaseView.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto.ts";

export default {
  name: "ChangeTruckStatusAction",
  components: { TruckChangeStatus, OmniDialog },
  mixins: [TrucksMixin, UsersMixin, BaseView],
  props: {
    truck: Object,
    closeOnSuccess: { type: Boolean, default: true },
    closeOnError: { type: Boolean, default: true },
  },
  emits: ["success", "error"],
  data() {
    return {
      dialogVisible: false,
      location: null,
      testLocation: null,
      date: null,
      status: null,
      truckStatuses: [
        { id: "a", name: this.$t("trucks.status-a") },
        { id: "w", name: this.$t("trucks.status-w") },
        { id: "n", name: this.$t("trucks.status-n") },
      ],
      changeTruckRequest: {},
    };
  },
  computed: {
    hasAccessToChangeTruckStatus() {
      return this.hasAccess(DispatcherPermissionValue.ChangeTruckStatus);
    },
    changeEnabled() {
      return (
        this.truck.status !== this.truckStatusInRoute &&
        this.truck.status !== this.truckStatusOnHold &&
        this.truck.driver &&
        this.truck.owner?.status !== this.driverStatusOnHold &&
        this.truck.driver?.status !== this.driverStatusOnHold &&
        this.truck.driver2?.status !== this.driverStatusOnHold
      );
    },
  },
  mounted() {
    this.updateTruckInfo();
  },
  methods: {
    getLocationFromTruckAvailableLocation() {
      const location = {};
      if (this.truck.availableLocation) {
        const availableLocation = this.truck.availableLocation;
        location.timezone = availableLocation.timezone;
        location.address = {
          city: availableLocation.city,
          state: availableLocation.state,
          zipCode: availableLocation.postcode,
          country: availableLocation.country,
        };
        location.fullAddress = availableLocation.fullAddress || "";
        if (availableLocation.coordinates) {
          location.lat = availableLocation.coordinates.lat;
          location.lon = availableLocation.coordinates.lon;
        }
      }
      return location;
    },
    showDialog() {
      this.updateTruckInfo();
      this.dialogVisible = true;
    },
    closeDialog() {
      this.dialogVisible = false;
    },
    async save() {
      const result = await this.$refs.form.validate();

      if (!result.valid) {
        return;
      }

      try {
        const response = await this.$api.trucks.updateTruckStatus(this.truck.id, this.changeTruckRequest);
        this.$emit("success", response.data);
        if (this.closeOnSuccess) {
          this.closeDialog();
        }
      } catch (error) {
        this.$emit("error");
        if (this.closeOnSuccess) {
          this.closeDialog();
        }
        console.error("Error save truck location: ", error);
      }
    },
    updateTruckInfo() {
      if (this.truck.id === undefined) return;

      this.date = null;
      this.location = null;
      this.status = this.truck.status;
      const location = this.getLocationFromTruckAvailableLocation();

      if (location.address?.city && location.address.city !== "Unknown") {
        this.location = location;
      }

      if (this.truck.availableDate && this.location) {
        const availableDate = this.$dayjs(this.truck.availableDate).tz(this.location.timezone);
        if (
          this.truck.status !== this.truckStatusNotAvailable ||
          (this.truck.status === this.truckStatusNotAvailable && availableDate.isAfter(this.$dayjs()))
        ) {
          this.date = availableDate.format("YYYY-MM-DD HH:mm");
        }
      }
    },
  },
};
</script>
<template>
  <div v-if="changeEnabled" @click="showDialog">
    <slot></slot>
  </div>
  <div v-if="!changeEnabled">
    <slot v-if="$slots.disabled" name="disabled">
      <v-tooltip location="top">
        <template #activator="{ props }">
          <v-btn class="mt-3 text-uppercase font-weight-600 width-100" color="grey" v-bind="props" variant="outlined">
            CHANGE STATUS
          </v-btn>
        </template>
        <span> The status cannot be changed when truck doesn't have a first driver </span>
      </v-tooltip>
    </slot>
    <slot v-else></slot>
  </div>
  <omni-dialog
    v-model="dialogVisible"
    :primary-button-disabled="!hasAccessToChangeTruckStatus"
    :primary-button-tooltip-text="$t('permission.change-truck-status')"
    :title="'Change status for truck #' + truck.number"
    has-primary-button-tooltip
    primary-action-label="Save"
    size="large"
    @primary-action:click="save"
  >
    <v-form ref="form">
      <truck-change-status v-model="changeTruckRequest" :truck="truck" />
    </v-form>
  </omni-dialog>
</template>
